export default function cartReducer(state, action) {
  const currentCart = state || createEmptyCart();

  // Add a flag to help distinguish between real and optimistic updates.
  // This is useful for reducing unnecessary re-renders
  currentCart.isOptimistic = true;

  try {
    switch (action.type) {
      case 'UPDATE_ITEM': {
        const { merchandiseId, updatedQuantity } = action.payload;

        const updatedLines = currentCart.lines
          .map((item) =>
            item.id === merchandiseId
              ? updateCartItemQuantity(item, updatedQuantity)
              : item
          )
          .filter(Boolean);

        return {
          ...currentCart,
          ...updateCartTotals(updatedLines),
          lines: updatedLines
        };
      }

      case 'DELETE': {
        const { merchandiseIdArray } = action.payload;
        const updatedLines = currentCart.lines.filter(
          (item) => !merchandiseIdArray.includes(String(item.id))
        );

        return {
          ...currentCart,
          ...updateCartTotals(updatedLines),
          lines: updatedLines
        };
      }

      case 'ADD_MULTIPLE_ITEMS': {
        const items = action.payload;
        const updatedLines = items.reduce(
          (lines, { variant, product, quantity }) => {
            const existingItem = lines.find(
              (item) => item.merchandise.id === variant.id
            );
            const updatedItem = createOrUpdateCartItem(
              existingItem,
              variant,
              product,
              quantity
            );

            if (existingItem) {
              return lines.map((item) =>
                item.merchandise.id === variant.id ? updatedItem : item
              );
            } else {
              return [...lines, updatedItem];
            }
          },
          currentCart.lines
        );

        return {
          ...currentCart,
          ...updateCartTotals(updatedLines),
          lines: updatedLines
        };
      }
      default:
        return currentCart;
    }
  } catch (err) {
    // Catching errors here helps since this runs in the topmost layout.js.
    // Returning currentCart here will allow the app to fallback to the real cart.
    console.error(`Could not use reducer to update optimistic cart: ${err}`);
    return currentCart;
  }
}

function updateCartItemQuantity(item, updatedQuantity) {
  if (updatedQuantity === 0) return null;

  const singleItemAmount = getSingleItemAmount(item);

  // If price data is missing, only update quantity and keep existing cost values to avoid breaking optimistic cart rendering.
  if (singleItemAmount === null) {
    return { ...item, quantity: updatedQuantity };
  }

  const newTotalAmount = calculateItemCost(
    updatedQuantity,
    singleItemAmount.toString()
  );
  const currencyCode = getLineCurrencyCode(item);

  return {
    ...item,
    quantity: updatedQuantity,
    cost: {
      totalAmount: {
        amount: newTotalAmount,
        currencyCode
      }
    }
  };
}

function createOrUpdateCartItem(
  existingItem,
  variant,
  product,
  quantity = 1 // Default to 1 if quantity is not provided
) {
  const newQuantity = existingItem
    ? existingItem.quantity + quantity
    : quantity;
  const totalAmount = calculateItemCost(newQuantity, variant.price.amount);
  return {
    id: existingItem?.id,
    quantity: newQuantity,
    attributes: existingItem?.attributes ?? [],
    cost: {
      totalAmount: {
        amount: totalAmount,
        currencyCode: variant.price.currencyCode
      }
    },
    merchandise: {
      id: variant.id,
      title: variant.title,
      selectedOptions: variant.selectedOptions,
      compareAtPrice: {
        amount: variant.compareAtPrice.amount,
        currencyCode: variant.compareAtPrice.currencyCode
      },
      price: {
        amount: variant.price.amount,
        currencyCode: variant.price.currencyCode
      },
      product: {
        id: product.id,
        availableForSale: product.availableForSale,
        handle: product.handle,
        title: product.title,
        featuredImage: product.featuredImage,
        productType: product.productType
      },
      sku: variant.sku
    }
  };
}

function updateCartTotals(lines) {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = lines.reduce(
    (sum, item) => sum + getLineTotalAmount(item),
    0
  );
  const currencyCode = getLineCurrencyCode(lines[0]);

  return {
    totalQuantity,
    cost: {
      subtotalAmount: { amount: totalAmount.toString(), currencyCode },
      totalAmount: { amount: totalAmount.toString(), currencyCode },
      totalTaxAmount: { amount: '0', currencyCode }
    }
  };
}

function createEmptyCart() {
  return {
    id: undefined,
    checkoutUrl: '',
    totalQuantity: 0,
    lines: [],
    cost: {
      subtotalAmount: { amount: '0', currencyCode: 'USD' },
      totalAmount: { amount: '0', currencyCode: 'USD' },
      totalTaxAmount: { amount: '0', currencyCode: 'USD' }
    }
  };
}

function getSingleItemAmount(item) {
  const totalAmount = item.cost?.totalAmount?.amount;

  if (totalAmount != null && item.quantity) {
    return Number(totalAmount) / item.quantity;
  }

  const merchandisePrice = item.merchandise?.price?.amount;
  if (merchandisePrice != null) {
    return Number(merchandisePrice);
  }

  return null;
}

function getLineTotalAmount(item) {
  const totalAmount = item.cost?.totalAmount?.amount;

  if (totalAmount != null) {
    return Number(totalAmount);
  }

  return Number(
    calculateItemCost(item.quantity, item.merchandise?.price?.amount ?? 0)
  );
}

function getLineCurrencyCode(item) {
  return (
    item?.cost?.totalAmount?.currencyCode ??
    item?.merchandise?.price?.currencyCode ??
    'USD'
  );
}

function calculateItemCost(quantity, price) {
  return (Number(price) * quantity).toString();
}
